import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { dramas } from '@/mocks/dramas';

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();

  const drama = useMemo(() => {
    return dramas.find((d) => d.id === id);
  }, [id]);

  const relatedDramas = useMemo(() => {
    if (!drama) return [];
    return dramas
      .filter((d) => d.categorySlug === drama.categorySlug && d.id !== drama.id)
      .slice(0, 6);
  }, [drama]);

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="font-heading text-2xl text-foreground-900">剧集不存在</h1>
          <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: `${drama.title} - 萝卜影视电视剧全集在线观看`,
          description: drama.description,
          url: `https://www.luobotv.com/drama/${drama.id}`,
          datePublished: `${drama.year}-01-01`,
          author: {
            '@type': 'Organization',
            name: '萝卜影视',
          },
          publisher: {
            '@type': 'Organization',
            name: '萝卜影视',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.luobotv.com/drama/${drama.id}`,
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: drama.category, href: `/category/${drama.categorySlug}` },
                  { label: drama.title },
                ]}
              />
            </div>

            {/* Drama Header */}
            <div className="flex flex-col md:flex-row gap-8 mb-14">
              {/* Poster */}
              <div className="flex-shrink-0 w-full md:w-72">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={drama.image}
                    alt={`${drama.title} 电视剧全集海报`}
                    title={`${drama.title} - 萝卜影视电视剧全集`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4">
                  {drama.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-600 mb-4 flex-wrap">
                  <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-medium">
                    <i className="ri-star-fill text-accent-500"></i>
                    {drama.rating}
                  </span>
                  <span>{drama.year}</span>
                  <span>{drama.category}</span>
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {drama.status}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {drama.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-8">
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0">导演：</span>
                    <span className="text-foreground-700">{drama.director}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0">主演：</span>
                    <span className="text-foreground-700">{drama.cast.join('、')}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0">集数：</span>
                    <span className="text-foreground-700">{drama.updateInfo}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-foreground-400 flex-shrink-0">状态：</span>
                    <span className="text-foreground-700">{drama.status}</span>
                  </div>
                </div>

                <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-fill text-xl"></i>
                  立即观看全集
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">剧情简介</h2>
              </div>
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                <p>{drama.content}</p>
              </div>
            </div>

            {/* Episode List */}
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">分集列表</h2>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {Array.from({ length: drama.episodes }, (_, i) => i + 1).map((ep) => (
                  <button
                    key={ep}
                    className="px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
            </div>

            {/* Related */}
            {relatedDramas.length > 0 && (
              <div className="border-t border-background-200/70 pt-14">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
                  <h2 className="font-heading text-xl text-foreground-900">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedDramas.map((rd) => (
                    <Link
                      key={rd.id}
                      to={`/drama/${rd.id}`}
                      className="group block cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2">
                        <img
                          src={rd.image}
                          alt={rd.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-1.5 py-0.5 rounded text-xs bg-accent-500 text-background-50">
                            {rd.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="font-heading text-xs text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rd.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}